import axios from "axios";
import userInfo from "../function/userInfo";
import { AddAccessTokenCache } from "../helper/cacheHelper";
import { ClearAllCacheFromIndexedDB } from "../IndexedDB/CacheIndexedDB";
import i18n from "../locales/i18n";
import { UserService } from "./ApiSevice";

let isRefreshing = false;
let failedQueue = [];

const ProcessQueue = () => {
  const token = userInfo.GetAccessToken();
  if (token && failedQueue.length > 0) {
    failedQueue.forEach((prom) => {
      prom.resolve();
    });
  }
  failedQueue = [];
};

const ReSendApiByNewAccessToken = (originalConfig) => {
  const token = userInfo.GetAccessToken();
  originalConfig.headers["Authorization"] = "Bearer " + token;
  return axios(originalConfig)
    .then((resCallback) => {
      return resCallback;
    })
    .catch(() => {
      location.href = `${window.location.origin}/SessionExpire`;
    });
};

const CreateAxiosInstanceWaitRecheckToken = (baseURL, upload = false) => {
  const instance = axios.create({
    baseURL,
    headers: {
      "Content-Type": !upload ? "application/json;charset=utf-8" : "multipart/form-data;charset=utf-8",
    },
  });

  instance.interceptors.request.use(
    (config) => {
      const token = userInfo.GetAccessToken();
      const merchantId = userInfo.MerchantId();
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      if (merchantId) {
        config.headers["merchantId"] = `${merchantId}`;
      }
      config.headers["language"] = i18n.locale;
      config.headers["Cache-Control"] = "no-store, no-cache, max-age=0, must-revalidate, proxy-revalidate";
      config.headers["CDN-Cache-Control"] = "no-store, no-cache, max-age=0, must-revalidate, proxy-revalidate";
      config.headers["timezoneOffset"] = userInfo.GetTimezoneOffset();

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
  instance.interceptors.response.use(
    (res) => {
      return res;
    },
    (err) => {
      const originalConfig = err.config;
      if (err.response && err.response.status === 401) {
        // Access Token was expire
        if (isRefreshing) {
          return new Promise((resolve) => {
            failedQueue.push({ resolve });
          }).then(() => {
            return ReSendApiByNewAccessToken(originalConfig);
          });
        }

        isRefreshing = true;

        return UserService.DoGetNewAccessTokenByRefreshToken()
          .then(async (resNewAccessTokenByRefreshToken) => {
            if (resNewAccessTokenByRefreshToken.status.IsSuccess()) {
              const keepLogin = userInfo.GetKeepLogin();
              await ClearAllCacheFromIndexedDB();
              await AddAccessTokenCache(resNewAccessTokenByRefreshToken.data.access_token);
              userInfo.SetCookiesAccessToken(resNewAccessTokenByRefreshToken.data.access_token);
              userInfo.SetCookiesRefreshToken(resNewAccessTokenByRefreshToken.data.refresh_token, keepLogin);
              return ReSendApiByNewAccessToken(originalConfig);
            } else {
              location.href = `${window.location.origin}/SessionExpire`;
            }
          })
          .catch((_error) => {
            userInfo.RemoveCookiesToken();
            location.href = `${window.location.origin}/SessionExpire`;
          })
          .finally(() => {
            ProcessQueue();
            isRefreshing = false;
          });
      }

      if (err.response && err.response.status === 429) {
        location.href = `${window.location.origin}/TooManyRequest`;
      }

      return Promise.reject(err);
    }
  );
  return instance;
};
const CreateAxiosInstance = (baseURL, upload = false) => {
  const instance = axios.create({
    baseURL,
    headers: {
      "Content-Type": !upload ? "application/json;charset=utf-8" : "multipart/form-data;charset=utf-8",
    },
  });

  instance.interceptors.request.use(
    (config) => {
      const token = userInfo.GetAccessToken();
      const merchantId = userInfo.MerchantId();
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      if (merchantId) {
        config.headers["merchantId"] = `${merchantId}`;
      }
      config.headers["language"] = i18n.locale;
      config.headers["Cache-Control"] = "no-store, no-cache, max-age=0, must-revalidate, proxy-revalidate";
      config.headers["CDN-Cache-Control"] = "no-store, no-cache, max-age=0, must-revalidate, proxy-revalidate";
      config.headers["timezoneOffset"] = userInfo.GetTimezoneOffset();

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
  instance.interceptors.response.use(
    (res) => {
      return res;
    },
    (err) => {
      return Promise.reject(err);
    }
  );
  return instance;
};

const peakAPI = CreateAxiosInstanceWaitRecheckToken(process.env.VUE_APP_PeakExportService);
const peakReportAPI = CreateAxiosInstanceWaitRecheckToken(process.env.VUE_APP_PeakExportServiceForReport);
const peakRealTimeAPIForUser = CreateAxiosInstanceWaitRecheckToken(process.env.VUE_APP_PeakRealTimeServiceForUser);
const peakRealTimeAPIForImport = CreateAxiosInstanceWaitRecheckToken(process.env.VUE_APP_PeakRealTimeServiceForImport);
const peakRealTimeAPIForTransaction = CreateAxiosInstanceWaitRecheckToken(
  process.env.VUE_APP_PeakRealTimeServiceForTransaction
);
const peakRealTimeAPI = CreateAxiosInstanceWaitRecheckToken(process.env.VUE_APP_PeakRealTimeService);
const peakRealTimeForUploadAPI = CreateAxiosInstanceWaitRecheckToken(process.env.VUE_APP_PeakRealTimeService, true);
const peakActiveCampaignAPI = CreateAxiosInstanceWaitRecheckToken(process.env.VUE_APP_PeakCheckActiveCampaign);
const peakLogAPI = CreateAxiosInstanceWaitRecheckToken(process.env.VUE_APP_PeakLogService);
const peakAuthenticationAPI = CreateAxiosInstance(process.env.VUE_APP_PeakAuthenService);
const peakAppConnect = CreateAxiosInstanceWaitRecheckToken(process.env.VUE_APP_PeakAppConnectService);
const peakAffiliatesConnect = CreateAxiosInstanceWaitRecheckToken(process.env.VUE_APP_PeakAffiliatesService);
const peakPDFLog = CreateAxiosInstanceWaitRecheckToken(process.env.VUE_APP_PeakPDFLogService);
const peakOnlineView = CreateAxiosInstanceWaitRecheckToken(process.env.VUE_APP_PeakOnlineViewService);
const peakFileService = CreateAxiosInstanceWaitRecheckToken(process.env.VUE_APP_PeakFileService);
const peakTaxService = CreateAxiosInstanceWaitRecheckToken(process.env.VUE_APP_PeakTaxService);

const peakVirtualCFOAPI = CreateAxiosInstanceWaitRecheckToken(process.env.VUE_APP_PeakVirtualCFOService);
export {
  peakActiveCampaignAPI,
  peakAffiliatesConnect,
  peakAPI,
  peakAppConnect,
  peakAuthenticationAPI,
  peakFileService,
  peakLogAPI,
  peakOnlineView,
  peakPDFLog,
  peakRealTimeAPI,
  peakRealTimeAPIForImport,
  peakRealTimeAPIForTransaction,
  peakRealTimeAPIForUser,
  peakRealTimeForUploadAPI,
  peakReportAPI,
  peakTaxService,
  peakVirtualCFOAPI,
};
